import {
  isRejectedWithValue,
  type Middleware,
  type MiddlewareAPI,
  type UnknownAction,
} from '@reduxjs/toolkit';
import { logout } from '../domains/auth/infrastructure/slices/authSlice';
import { logOut } from './reducers/authorization.reducer';
import { RouteEnum } from '../app/routes/routeEnum';

const AUTH_ENDPOINT_NAMES = new Set([
  'signIn',
  'signUp',
  'forgotPassword',
  'signInWithGoogle',
  'signUpWithGoogle',
  // Bootstrap probe: 401 means unauthenticated, not a mid-session expiry.
  'whoAmI',
]);

function isAuthEndpoint(action: UnknownAction): boolean {
  const meta = action.meta as
    | { arg?: { endpointName?: string } }
    | undefined;
  const name = meta?.arg?.endpointName;
  return Boolean(name && AUTH_ENDPOINT_NAMES.has(name));
}

function redirectToSignIn(): void {
  if (typeof window === 'undefined') return;
  if (
    window.location.pathname.includes('sign-in') ||
    window.location.pathname.includes('login')
  ) {
    return;
  }
  window.location.assign(RouteEnum.SIGN_IN);
}

/**
 * Clears session when any RTK Query request returns HTTP 401
 * (except auth login/sign-up mutations).
 */
export const unauthorizedMiddleware: Middleware =
  (api: MiddlewareAPI) => (next) => (action) => {
    const result = next(action);

    if (!isRejectedWithValue(action) || isAuthEndpoint(action)) {
      return result;
    }

    const status = (action.payload as { status?: number | string } | undefined)
      ?.status;
    if (Number(status) !== 401) {
      return result;
    }

    const state = api.getState() as {
      auth?: { accessToken?: string | null; user?: unknown };
      authorization?: { accessToken?: string | null; user?: unknown };
    };
    const hasSession = Boolean(
      state.auth?.accessToken ||
        state.authorization?.accessToken ||
        state.auth?.user ||
        state.authorization?.user,
    );
    if (!hasSession) {
      return result;
    }

    api.dispatch(logout());
    api.dispatch(logOut());
    redirectToSignIn();
    return result;
  };
